/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import GlobalGroupsSearch from '@ion/features/src/global-groups/search';
import {FilterSearch} from '../../filters/FilterSearch';
import {useIsPhone} from '../../hooks/useMedia';
import {dlsColor, dlsZIndex} from '../../theme/theme';
import {DLSTheme} from '../../types/types';
import {renderTimeout} from './GroupChooserUtils';

export interface GlobalGroupChooserSearchProps {
  setSearchValue: Function;
  setRenderingData: Function;
  clearSearch?: boolean;
  listLength?: number;
  inputLength?: number;
  onlyAccountId?: boolean;
}

const useStyles = makeStyles((theme: DLSTheme) => ({
  globalGroupSearch : {
    padding                        : '12px 0',
    [theme.breakpoints.down('sm')] : {
      padding : ' 6px 0'
    }
  },
  inputOverlay : {
    position       : 'absolute',
    right          : '100px',
    color          : dlsColor('grey4'),
    fontSize       : '.8rem',
    height         : '40px',
    zIndex         : dlsZIndex('globalGroupChooserSearchInputOverlay'),
    display        : 'flex',
    justifyContent : 'flex-end',
    alignItems     : 'center'
  }
}));

/**
 * Abstraction of the Filter Search component to fit the global group
 * search for desktop and mobile
 *
 * ```typescript
 * import {GlobalGroupChooserSearch} from '@ion/dls/src/components/groupchooser';
 *
 *   <GlobalGroupChooserSearch setSearchValue={setSearchValue}
 *     clearSearch={false} />
 * ```
 */
export const GlobalGroupChooserSearch = ({
  setSearchValue,
  clearSearch = false,
  listLength = 0,
  inputLength = 3,
  setRenderingData,
  onlyAccountId = false
}: GlobalGroupChooserSearchProps) => {
  const classes = useStyles();
  const [valueLength, setValueLength]   = useState(0);
  const [showOverlay, setShowOverlay]   = useState('');
  const [disableInput, setDisableInput] = useState(false);
  const isPhone                         = useIsPhone();

  const debounceSearch = debounce((value) => {
    const longList           = listLength >= 75;
    const valLength          = value.length;
    const shortInput         = value.length < inputLength;
    const shortExistingValue = valueLength < inputLength;
    if (!shortInput|| !longList) {
      // list is either short enough or the
      setShowOverlay('');
      setSearchValue(value);
      GlobalGroupsSearch.findResults(value, onlyAccountId);
    } else if (valLength && shortInput && shortExistingValue && longList) {
      // user has started typing but the search string is too short
      setShowOverlay(translate('common:search.keepTyping'));
    } else if (shortInput && !shortExistingValue && longList) {
      // user has deleted the search value to a point where it's too short
      // to perform a search and we need to reload the list, which takes time
      loadingData();
      GlobalGroupsSearch.findResults('');
    } else if (!valLength) {
      // make sure we don't show any overlays when there is no search value
      setShowOverlay('');
      GlobalGroupsSearch.findResults('');
    }
    setValueLength(valLength);
  }, 250);

  function loadingData() {
    setShowOverlay(translate('common:states.oneMoment'));
    setDisableInput(true);
    setRenderingData(true);
    setSearchValue('');
    setTimeout(() => {
      setRenderingData(false);
      setShowOverlay('');
      setDisableInput(false);
    }, renderTimeout(listLength));
  }

  function onChange(value) {
    debounceSearch(value);
  }

  return (
    <div className={classes.globalGroupSearch}>
      {showOverlay && (
        <span className={classes.inputOverlay}>
          {showOverlay}
        </span>
      )}
      <FilterSearch clearSearch={clearSearch}
        suppressAutoFocusOnOpen={isPhone}
        onChange={onChange}
        disabled={disableInput}
        openFilterSearch
        fullWidth />
    </div>
  );
};

export default GlobalGroupChooserSearch;
